// ═══════════════════════════════════════════════════════════════
// VolunteerOS — Settings
// Organization settings, check-in config, thresholds, admin mgmt
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback } from 'react';
import {
  Title, Text, Paper, Stack, Tabs, TextInput, NumberInput, Switch,
  Table, Badge, ActionIcon, Tooltip,
} from '@mantine/core';
import {
  IconBuilding, IconLogin, IconGauge, IconBell, IconShield,
  IconCrown, IconUserMinus,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useVolunteerStore, parseExtras, mergeExtras } from '../../store/volunteerStore';
import type { Volunteer } from '../../store/volunteerStore';

// ── Types ────────────────────────────────────────────────────

interface OrgSettings {
  orgName: string;
  contactEmail: string;
  contactPhone: string;
  website: string;
  address: string;
}

interface CheckInSettings {
  selfCheckin: boolean;
  requireGeo: boolean;
  autoCheckOut: boolean;
  geoRadius: number;
  windowMinutes: number;
}

interface ThresholdSettings {
  minRestHours: number;
  maxConsecutiveDays: number;
}

interface NotificationSettings {
  emailEnabled: boolean;
  smsEnabled: boolean;
}

interface AllSettings {
  org: OrgSettings;
  checkIn: CheckInSettings;
  thresholds: ThresholdSettings;
  notifications: NotificationSettings;
}

const LS_KEY = 'vol-org-settings';

const DEFAULTS: AllSettings = {
  org: { orgName: '', contactEmail: '', contactPhone: '', website: '', address: '' },
  checkIn: { selfCheckin: true, requireGeo: false, autoCheckOut: false, geoRadius: 100, windowMinutes: 15 },
  thresholds: { minRestHours: 8, maxConsecutiveDays: 7 },
  notifications: { emailEnabled: true, smsEnabled: false },
};

function loadSettings(): AllSettings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AllSettings>;
      return {
        org: { ...DEFAULTS.org, ...parsed.org },
        checkIn: { ...DEFAULTS.checkIn, ...parsed.checkIn },
        thresholds: { ...DEFAULTS.thresholds, ...parsed.thresholds },
        notifications: { ...DEFAULTS.notifications, ...parsed.notifications },
      };
    }
  } catch { /* ignore */ }
  return { ...DEFAULTS };
}

function persistSettings(settings: AllSettings): void {
  localStorage.setItem(LS_KEY, JSON.stringify(settings));
}

// ── Component ────────────────────────────────────────────────

export default function Settings() {
  const { volunteers, loadVolunteers, updateVolunteer } = useVolunteerStore();

  const [loaded, setLoaded] = useState(false);
  const [settings, setSettings] = useState<AllSettings>(() => loadSettings());

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      loadVolunteers();
    }
  }, [loaded, loadVolunteers]);

  // Persist on every change
  const update = useCallback(<K extends keyof AllSettings>(section: K, patch: Partial<AllSettings[K]>) => {
    setSettings(prev => {
      const next = {
        ...prev,
        [section]: { ...prev[section], ...patch },
      };
      persistSettings(next);
      return next;
    });
  }, []);

  // ── Admin toggle ──────────────────────────────────────────

  const toggleAdmin = useCallback(async (vol: Volunteer) => {
    const extras = parseExtras(vol.notes);
    const isAdmin = extras.admin === true;
    const newNotes = mergeExtras(vol.notes, { admin: !isAdmin });
    try {
      await updateVolunteer(vol.id, { notes: newNotes });
      notifications.show({
        title: isAdmin ? 'Admin Removed' : 'Admin Granted',
        message: `${vol.firstName} ${vol.lastName} ${isAdmin ? 'is no longer' : 'is now'} an admin.`,
        color: isAdmin ? 'gray' : 'green',
      });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to update admin status.', color: 'red' });
    }
  }, [updateVolunteer]);

  // ── Render ────────────────────────────────────────────────

  const { org, checkIn, thresholds, notifications: notifSettings } = settings;

  return (
    <Stack gap="lg">
      <Title order={3} c="black">Settings</Title>

      <Paper p="md" radius="sm" withBorder>
        <Tabs defaultValue="org">
          <Tabs.List>
            <Tabs.Tab value="org" leftSection={<IconBuilding size={14} />}>Organization</Tabs.Tab>
            <Tabs.Tab value="checkin" leftSection={<IconLogin size={14} />}>Check-In</Tabs.Tab>
            <Tabs.Tab value="thresholds" leftSection={<IconGauge size={14} />}>Thresholds</Tabs.Tab>
            <Tabs.Tab value="notif" leftSection={<IconBell size={14} />}>Notifications</Tabs.Tab>
            <Tabs.Tab value="admin" leftSection={<IconShield size={14} />}>Admin Management</Tabs.Tab>
          </Tabs.List>

          {/* Organization */}
          <Tabs.Panel value="org" pt="md">
            <Stack gap="sm">
              <TextInput
                label="Organization Name"
                placeholder="Your organization"
                value={org.orgName}
                onChange={e => update('org', { orgName: e.currentTarget.value })}
              />
              <TextInput
                label="Contact Email"
                placeholder="contact@example.org"
                value={org.contactEmail}
                onChange={e => update('org', { contactEmail: e.currentTarget.value })}
              />
              <TextInput
                label="Contact Phone"
                placeholder="(555) 555-5555"
                value={org.contactPhone}
                onChange={e => update('org', { contactPhone: e.currentTarget.value })}
              />
              <TextInput
                label="Website"
                placeholder="https://example.org"
                value={org.website}
                onChange={e => update('org', { website: e.currentTarget.value })}
              />
              <TextInput
                label="Address"
                placeholder="123 Main St, City, ST 00000"
                value={org.address}
                onChange={e => update('org', { address: e.currentTarget.value })}
              />
              <Text size="xs" c="dimmed">Settings are saved automatically.</Text>
            </Stack>
          </Tabs.Panel>

          {/* Check-In */}
          <Tabs.Panel value="checkin" pt="md">
            <Stack gap="sm">
              <Switch
                label="Self Check-In"
                description="Allow volunteers to check themselves in"
                checked={checkIn.selfCheckin}
                onChange={e => update('checkIn', { selfCheckin: e.currentTarget.checked })}
              />
              <Switch
                label="Require Geolocation"
                description="Require GPS verification at check-in"
                checked={checkIn.requireGeo}
                onChange={e => update('checkIn', { requireGeo: e.currentTarget.checked })}
              />
              <Switch
                label="Auto Check-Out"
                description="Automatically check out at shift end"
                checked={checkIn.autoCheckOut}
                onChange={e => update('checkIn', { autoCheckOut: e.currentTarget.checked })}
              />
              <NumberInput
                label="Geofence Radius (meters)"
                description="Maximum distance from site for check-in"
                value={checkIn.geoRadius}
                onChange={v => update('checkIn', { geoRadius: typeof v === 'number' ? v : 100 })}
                min={10}
                max={5000}
                step={10}
              />
              <NumberInput
                label="Check-In Window (minutes)"
                description="How early/late a volunteer can check in"
                value={checkIn.windowMinutes}
                onChange={v => update('checkIn', { windowMinutes: typeof v === 'number' ? v : 15 })}
                min={5}
                max={120}
                step={5}
              />
              <Text size="xs" c="dimmed">Settings are saved automatically.</Text>
            </Stack>
          </Tabs.Panel>

          {/* Thresholds */}
          <Tabs.Panel value="thresholds" pt="md">
            <Stack gap="sm">
              <NumberInput
                label="Minimum Rest Hours"
                description="Required hours between shifts"
                value={thresholds.minRestHours}
                onChange={v => update('thresholds', { minRestHours: typeof v === 'number' ? v : 8 })}
                min={1}
                max={48}
              />
              <NumberInput
                label="Max Consecutive Days"
                description="Maximum days a volunteer can work in a row"
                value={thresholds.maxConsecutiveDays}
                onChange={v => update('thresholds', { maxConsecutiveDays: typeof v === 'number' ? v : 7 })}
                min={1}
                max={30}
              />
              <Text size="xs" c="dimmed">Settings are saved automatically.</Text>
            </Stack>
          </Tabs.Panel>

          {/* Notifications */}
          <Tabs.Panel value="notif" pt="md">
            <Stack gap="sm">
              <Switch
                label="Email Notifications"
                description="Enable email delivery for broadcasts"
                checked={notifSettings.emailEnabled}
                onChange={e => update('notifications', { emailEnabled: e.currentTarget.checked })}
              />
              <Switch
                label="SMS Notifications"
                description="Enable SMS delivery for broadcasts"
                checked={notifSettings.smsEnabled}
                onChange={e => update('notifications', { smsEnabled: e.currentTarget.checked })}
              />
              <Paper p="sm" radius="sm" bg="var(--mantine-color-blue-light)">
                <Text size="xs">
                  Email and SMS delivery is handled by the OpenGov Worker proxy using AWS SES and SNS.
                  These toggles control whether the Comms page offers those channels by default.
                  Actual delivery configuration (verified sender, SNS topics) is managed at the worker level.
                </Text>
              </Paper>
              <Text size="xs" c="dimmed">Settings are saved automatically.</Text>
            </Stack>
          </Tabs.Panel>

          {/* Admin Management */}
          <Tabs.Panel value="admin" pt="md">
            <Stack gap="sm">
              <Text size="sm" c="dimmed">
                Mark volunteers as admins. Admins have full access to settings and management features.
              </Text>

              {volunteers.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="md">No volunteers found.</Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Admin</Table.Th>
                      <Table.Th>Action</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {volunteers.map(v => {
                      const isAdmin = parseExtras(v.notes).admin === true;
                      return (
                        <Table.Tr key={v.id}>
                          <Table.Td>{v.firstName} {v.lastName}</Table.Td>
                          <Table.Td><Text size="xs">{v.email}</Text></Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              variant="light"
                              color={v.status === 'active' ? 'green' : v.status === 'inactive' ? 'gray' : 'yellow'}
                            >
                              {v.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            {isAdmin ? (
                              <Badge size="sm" color="violet" variant="filled">Admin</Badge>
                            ) : (
                              <Text size="xs" c="dimmed">-</Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Tooltip label={isAdmin ? 'Remove admin' : 'Grant admin'}>
                              <ActionIcon
                                variant={isAdmin ? 'light' : 'subtle'}
                                color={isAdmin ? 'red' : 'green'}
                                size="sm"
                                onClick={() => toggleAdmin(v)}
                              >
                                {isAdmin ? <IconUserMinus size={14} /> : <IconCrown size={14} />}
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </Stack>
  );
}
